<template>
  <div
    v-if="specialRequest"
    class="container"
  >
    <div class="md:mb-6">
      <div class="md:flex justify-content-between align-items-center">
        <h2>{{ $t('specialrequestupdatePage.title') }}</h2>
        <Button
          :label="$t('action.back')"
          icon="pi pi-arrow-left"
          class="p-button-secondary p-button-rounded p-button-outlined mb-2 md:mb-0"
          @click="toSpecialRequestTable"
        />
      </div>
      <Message
        v-if="isAnswered"
        :closable="false"
        severity="info"
      >
        {{ $t('specialrequestupdatePage.alreadyAnswered') }}
      </Message>


      <Panel
        :header="$t('specialrequestupdatePage.details')"
        toggleable
        class="mb-4"
      >
        <div class="card_details ">
          <div class="card_body">
            <ul class="flex justify-content-between">
              <li v-if="specialRequest.reasonRequest">
                {{ $t('entity.specialrequest.reasonRequest') }} :
                <strong>{{ $t(EReasonRequest[specialRequest.reasonRequest]) }}</strong>
              </li>
              <li v-if="specialRequest.reasonRequest === 'LOCALIZING_MATERIALS'">
                {{ $t('entity.specialrequest.localizingMaterials') }} :
                <strong>{{ $t(ELocalizingMaterials[specialRequest.localizingMaterials]) }}</strong>
              </li>
              <li v-if="specialRequest.reasonRequest === 'ON_BOARD_EQUIPMENT_INSIDE_VEHICULE' && specialRequest.vehicleTypes?.length > 0">
                {{ $t('entity.specialrequest.vehicleTypesLabel') }} :
                <strong>{{ formatVehicleTypes(specialRequest.vehicleTypes) }}</strong>
              </li>
            </ul>
            <ul class="flex justify-content-between">
              <li v-if="specialRequest.detailsOtherLocalizingMaterials">
                {{ $t('entity.specialrequest.detailsOtherLocalizingMaterials') }} :
                <strong>{{ specialRequest.detailsOtherLocalizingMaterials }}</strong>
              </li>
              <li v-if="specialRequest.detailsOtherVehiculeType">
                {{ $t('entity.specialrequest.detailsOtherVehiculeType') }} :
                <strong>{{ specialRequest.detailsOtherVehiculeType }}</strong>
              </li>
            </ul>
            <ul class="col-12">
              <li
                v-if="specialRequest.commentary"
                class="w-full text-justify"
                style="word-break: break-word"
              >
                {{ $t('entity.specialrequest.commentary') }} :
                <strong>{{ specialRequest.commentary }}</strong>
              </li>
            </ul>
            <ul class="flex justify-content-between">
              <li v-if="specialRequest.startingAvailabilityDatetime">
                {{ $t('entity.specialrequest.startAvailabilityDatetime') }} :
                <strong>{{ formatDateTime(specialRequest.startingAvailabilityDatetime) }}</strong>
              </li>
              <li v-if="specialRequest.endingAvailabilityDateTime">
                {{ $t('entity.specialrequest.endAvailabilityDatetime') }} :
                <strong>{{ formatDateTime(specialRequest.endingAvailabilityDateTime) }}</strong>
              </li>
            </ul>
          </div>
        </div>
      </Panel>

      <Panel
        :header="$t('specialrequestupdatePage.contactOnSite')"
        toggleable
        class="mb-4"
      >
        <div class="card_details ">
          <div class="card_body">
            <ul class="flex justify-content-between">
              <li v-if="specialRequest.specialRequestContact && specialRequest.specialRequestContact.firstName">
                {{ $t('entity.specialrequest.specialRequestContact.firstName') }} :
                <strong>{{ specialRequest.specialRequestContact.firstName }}</strong>
              </li>
              <li v-if="specialRequest.specialRequestContact && specialRequest.specialRequestContact.lastName">
                {{ $t('entity.specialrequest.specialRequestContact.lastName') }} :
                <strong>{{ specialRequest.specialRequestContact.lastName }}</strong>
              </li>
            </ul>
            <ul class="flex justify-content-between">
              <li v-if="specialRequest.specialRequestContact && specialRequest.specialRequestContact.email">
                {{ $t('entity.specialrequest.specialRequestContact.email') }} :
                <strong>{{ specialRequest.specialRequestContact.email }}</strong>
              </li>
              <li v-if="specialRequest.specialRequestContact && specialRequest.specialRequestContact.phoneNumber">
                {{ $t('entity.specialrequest.specialRequestContact.phoneNumber') }} :
                <strong>{{ formatPhoneNumberAsInternational(specialRequest.specialRequestContact.phoneNumber, specialRequest.specialRequestContact.phoneNumberCountryCode) }}</strong>
              </li>
            </ul>
            <ul class="flex justify-content-between">
              <li v-if="specialRequest.specialRequestContact && specialRequest.specialRequestContact.profession">
                {{ $t('entity.specialrequest.specialRequestContact.profession') }} :
                <strong>{{ specialRequest.specialRequestContact.profession }}</strong>
              </li>
              <li v-if="specialRequest.specialRequestContact && specialRequest.specialRequestContact.company">
                {{ $t('entity.specialrequest.specialRequestContact.company') }} :
                <strong>{{ specialRequest.specialRequestContact.company }}</strong>
              </li>
            </ul>
          </div>
        </div>
      </Panel>

      <div class="p-input mb-4">
        <label
          for="request-answer"
          class="required-input"
        >
          {{ $t('entity.specialrequest.answer') }} :
        </label>
        <Textarea
          id="request-answer"
          v-model="v$.answer.$model"
          name="request-answer"
          :class="{'invalid-input': v$.answer.$error }"
          rows="10"
          :auto-resize="true"
          type="text"
          class="col-12 mt-2"
          required
          :disabled="isAnswered"
        />
        <div
          v-for="error of v$.answer.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div v-show="!isAnswered && !createdRequest">
        <EquipmentTypesField v-if="specialRequest.specialRequestsEquipments" :model-value="specialRequest.specialRequestsEquipments" :is-read-only="true" />

        <p>{{ $t('appointmentPage.appointments.selectInstruction') }}</p>

        <request-remaining-duration
          :appointments="appointments"
          :control-duration="totalDuration"
          :is-special-request="true"
          @duration-updated="checkValueReceived($event)"
        />

        <RoamingTeamCalendarsTabView
          v-model="appointments"
          :control-duration="totalDuration"
        />

        <request-appointments
          v-model:appointments="appointments"
          :control-duration="totalDuration"
          :user="specialRequest.contact"
          :equipments-number="nbEquipments"
          :appointment-on-office="false"
          @booked="onBooked"
        />

        <div class="p-input mb-3">
          <label
            for="wishedMeetingAddress"
            class="required-input"
          >{{ $t('entity.specialrequest.wishedMeetingAddress') }}</label>
          <InputAddress
            id="wishedMeetingAddress"
            v-model="address"
            class="col-12 mt-2"
            type="text"
          />
        </div>
      </div>
    </div>
  </div>
  <div
    v-if="specialRequest && !isAnswered"
    class="z-5 py-4 md:fixed md:bottom-0 md:w-full bg-white md:shadow-2"
  >
    <div class="flex justify-content-evenly flex-wrap">
      <Button
        :disabled="rejectButtonDisabled"
        class="p-button-rounded p-button-outlined"
        :label="$t('action.reject')"
        icon="pi pi-times"
        :loading="isSaving"
        @click="sendUpdate(false)"
      />

      <Button
        :disabled="acceptButtonDisabled"
        class="p-button-rounded p-button-outlined"
        :label="$t('action.confirm')"
        icon="pi pi-check"
        :loading="isSaving"
        @click="sendUpdate(true)"
      />
    </div>
  </div>
</template>

<script>
import RequestAppointments from "@/components/appointments/RequestAppointments.vue";
import Panel from "primevue/panel";
import ESpecialRequestStatus from "@/models/enums/ESpecialRequestStatus";
import Message from "primevue/message";
import Button from "primevue/button";
import SpecialRequestUpdateRequest from "@/models/dto/requests/SpecialRequestUpdateRequest";
import RequestRemainingDuration from "@/components/appointments/RequestRemainingDuration.vue";
import Textarea from "primevue/textarea";
import {useVuelidate} from "@vuelidate/core";
import {required} from "@/i18n/i18nValidators";
import RoamingTeamCalendarsTabView from "@/components/appointments/RoamingTeamCalendarsTabView.vue";
import EquipmentTypesField from "@/components/equipmenttype/EquipmentTypesField.vue";
import InputAddress from "@/components/InputAddress.vue";
import {formatPhoneNumberAsInternational} from "@/shared/utils/phoneNumberUtils";
import EReasonRequest from "../models/enums/EReasonRequest";
import ELocalizingMaterials from "../models/enums/ELocalizingMaterials";
import EVehicleType from "@/models/enums/EVehicleType";

export default {
  name: 'SpecialRequestUpdateView',
  components: {
    InputAddress,
    EquipmentTypesField, RequestRemainingDuration, RequestAppointments, RoamingTeamCalendarsTabView, Textarea, Panel, Message, Button
  },
  setup() {
    return {
      v$: useVuelidate({
        $scope: false
      })
    }
  },
  validations() {
    return {
      answer: {required}
    }
  },
  data() {
    return {
      /** @type {SpecialRequestFullDto}*/
      specialRequest: null,
      appointments: [],
      totalDuration: 0,
      answer: null,
      createdRequest: null,
      address: null,
      isSaving: false,
      nbEquipments: 0
    }
  },
  computed: {
    EReasonRequest() {
      return EReasonRequest
    },
    ELocalizingMaterials() {
      return ELocalizingMaterials
    },
    rejectButtonDisabled(){
      return this.v$.answer.$error || this.isSaving
    },
    acceptButtonDisabled() {
      return this.v$.answer.$error || !this.createdRequest || this.isSaving
    },
    isAnswered() {
      return this.specialRequest.status !== ESpecialRequestStatus.PENDING
    },
  },
  async created() {
    const requestId = this.$route.params.id;
    if (!requestId) {
      this.$router.push(-1)
    }
    this.specialRequest = await this.$specialRequestService.findById(requestId);
    this.answer = this.specialRequest.answer;
    this.address = this.specialRequest.wishedMeetingAddress;
    this.calculateAmountOfEquipments();
  },
  methods: {
    formatPhoneNumberAsInternational,
    /**
     * Envoi de la réponse
     * @param {boolean} accept vrai si la demande est acceptée
     */
    async sendUpdate(accept) {
      const isFormValid = await this.v$.$validate();
      if (!isFormValid) return;

      const update = new SpecialRequestUpdateRequest({
        id: this.specialRequest.id,
        answer: this.answer,
        status: accept ? ESpecialRequestStatus.ACCEPTED : ESpecialRequestStatus.REJECTED,
        requestId: this.createdRequest?.id,
        address: this.address
      })

      try{
        this.isSaving = true
        await this.$specialRequestService.update(update)
        this.$emitter.emit('toast', {severity: 'success', message: this.$t('specialrequestupdatePage.toast.success')})
        this.$router.push({name: 'specialRequest'})
      } catch (e) {
        const errors = e.response.data.map(error => this.$t(error)).join(', ')
        this.$emitter.emit('toast', {severity: 'error', message: errors})
      }finally {
        this.isSaving = false
      }
    },

    onBooked(request) {
      this.createdRequest = request
    },

    checkValueReceived(value){
      this.totalDuration = value;
      return true;
    },

    calculateAmountOfEquipments() {
      this.nbEquipments = 0;
      this.specialRequest.specialRequestsEquipments.forEach((equipment) => {
        this.nbEquipments+= equipment.quantity;
      });
    },
    formatDateTime(date) {
      return new Date(date).toLocaleString(this.$i18n.locale)
    },
    formatVehicleTypes(vehicleTypes) {
      return vehicleTypes.map(vehicle => this.$t(EVehicleType[vehicle])).join(', ');
    },
    toSpecialRequestTable() {
      this.$router.push({ name: 'specialRequest' })
    }
  }
}
</script>

<style scoped lang="scss">
.card_details {
  span {
    float: none;
  }

  ul,
  ol {
    list-style: none;
    padding: 0;
  }

  .card_header {
    .card_times {
      background: #f7f7f7;
      padding: 10px;
      font-weight: 600;
    }
  }

  .card_body {
    li {
      width: 25vw;
      padding: 5px 0;
    }
  }
}
</style>
